import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { format } from 'date-fns';
import { routes } from '../../../utils/constants';
import {
  ExpandCellContainer, ReversedIcon, ProgressBarContainer, ProgressBarBackground, ProgressBar, ProgressBarSpacerContainer,
} from './css';
import ChevronSmallDown from '../../../assets/icons/ChevronSmallDown.svg';
import { PrintingLabels } from '../../../utils/enums';
import { apiDownloadBol, apiDownloadPdfLabel, apiDownloadQuoteReport } from '../../../services/documents';
import ActionsMenu from '../actionsMenu';

export const TimeCellComponent = (props) => {
  const { time } = props;

  if (time) {
    return format(time, t('DATE_COLUMN_PATTERN'));
  }

  return ('--');
};

export const ExpandCellComponent = (props) => {
  const { reverse } = props;

  return (
    <ExpandCellContainer style={{
      display: 'flex', flexDirection: 'row', gap: '10px', justifyContent: 'center',
    }}
    >
      <p>{t('Updates')}</p>
      {reverse ? <ReversedIcon src={ChevronSmallDown} alt="Collapse" /> : <img src={ChevronSmallDown} alt="Expand" />}
    </ExpandCellContainer>
  );
};

export const OrderStatusCellComponent = (props) => {
  const { percentage, status, color } = props;

  let perc = 0;
  if (percentage) {
    if (percentage < 0) {
      perc = 0;
    } else if (percentage > 100) {
      perc = 100;
    } else {
      perc = percentage;
    }
  }

  return (
    <ProgressBarSpacerContainer>
      <ProgressBarContainer>
        <ProgressBarBackground />
        <ProgressBar progress={perc} color={color} />
      </ProgressBarContainer>
      <ProgressBarSpacerContainer>
        <p>{status}</p>
      </ProgressBarSpacerContainer>
    </ProgressBarSpacerContainer>
  );
};

export const ActionsDropdown = (props) => {
  const { data, options } = props;
  const probillNumber = (data.orderId) ? data.orderId : data.freight;

  const gotoShipmentDetails = () => {
    window.open(`${routes.viewOrder}?probillNumber=${probillNumber}`);
  };

  const gotoTrackingDetails = () => {
    window.open(`${routes.viewShipmentTracking}?orderId=${data.orderId}`);
  };

  const gotoDownloadBOL = () => {
    if (data.shipmentNumber) {
      apiDownloadBol(data.shipmentNumber);
    }
  };

  const gotoDownloadLabelsSM = () => {
    if (data.shipmentNumber) {
      apiDownloadPdfLabel(data.shipmentNumber, PrintingLabels.SM);
    }
  };

  const gotoDownloadLabelsLG = () => {
    if (data.shipmentNumber) {
      apiDownloadPdfLabel(data.shipmentNumber, PrintingLabels.LG);
    }
  };

  const gotoDuplicateOrder = () => {
    window.open(
      `${routes.createShipment}?recreate=true&probillNumber=${data.orderId}&shipmentNumber=${data?.shipmentNumber || ''}`,
    );
  };

  const gotoModifyOrder = () => {
    const ModifyOpt = options.filter((opt) => opt.id === 'ModifyOrder');
    if (ModifyOpt.length > 0) {
      const modifyAction = ModifyOpt[0].action;
      modifyAction(data.orderId);
    }

    return null;
  };

  const gotoCancelOrder = () => {
    const cancelOpt = options.filter((opt) => opt.id === 'CancelOrder');
    if (cancelOpt.length > 0) {
      const cancelAction = cancelOpt[0].action;
      cancelAction(data.orderId);
    }

    return null;
  };

  const printQuote = () => {
    apiDownloadQuoteReport(data.quoteNumber);
  };

  const createShipmentFromQuote = () => {
    window.open(`${routes.createShipment}?quoteNumber=${data.quoteNumber}`);
  };

  const createNewQuoteFromThisQuote = () => {
    window.open(`${routes.createQuote}?quoteNumber=${data.quoteNumber}`);
  };

  const checkDisabled = (actions, actionId) => {
    let isDisabled = false;
    if (actions) {
      const action = actions[actionId];
      if (!action) {
        isDisabled = false;
      }
    }

    return isDisabled;
  };

  const isPrintQuoteDisabled = data.status === 'Expired' || (!data.isApproved && !data.expired);

  const actionsCollection = [
    {
      id: 'PrintQuote',
      label: t('Print'),
      icon: 'PrintIcon',
      action: printQuote,
      disabled: isPrintQuoteDisabled || checkDisabled(data.actions, 'printQuote'),
      visible: (options.map((opt) => opt.id).indexOf('PrintQuote') !== -1),
    },
    {
      id: 'CreateShipmentFromQuote',
      label: t('CreateShipment'),
      icon: 'CreateShipmentIcon',
      action: createShipmentFromQuote,
      disabled: data.isApproved ? checkDisabled(data.actions, 'createShipmentFromQuote') : true,
      visible: (options.map((opt) => opt.id).indexOf('CreateShipmentFromQuote') !== -1),
    },
    {
      id: 'CreateNewQuoteFromThisQuote',
      label: t('Duplicate'),
      icon: 'DuplicateIcon',
      action: createNewQuoteFromThisQuote,
      disabled: checkDisabled(data.actions, 'createNewQuoteFromThisQuote'),
      visible: (options.map((opt) => opt.id).indexOf('CreateNewQuoteFromThisQuote') !== -1),
    },
    {
      id: 'ShipmentDetails',
      label: t('ViewShipmentDetails'),
      icon: 'ShipmentIcon',
      action: gotoShipmentDetails,
      disabled: checkDisabled(data.actions, 'shipmentDetails'),
      visible: (options.map((opt) => opt.id).indexOf('ShipmentDetails') !== -1),
    },
    {
      id: 'TrackingDetails',
      label: t('TrackingDetails'),
      icon: 'TrackIcon',
      action: gotoTrackingDetails,
      disabled: checkDisabled(data.actions, 'trackingDetails'),
      visible: (options.map((opt) => opt.id).indexOf('TrackingDetails') !== -1),
    },
    {
      id: 'DownloadBOL',
      label: t('DownloadBOL'),
      icon: 'DownloadIcon',
      action: gotoDownloadBOL,
      disabled: checkDisabled(data.actions, 'downloadBOL'),
      visible: (options.map((opt) => opt.id).indexOf('DownloadBOL') !== -1),
    },
    {
      id: 'DownloadLabelsSM',
      label: t('DownloadLabelsSM'),
      icon: 'DownloadIcon',
      action: gotoDownloadLabelsSM,
      disabled: checkDisabled(data.actions, 'DownloadLabelsSM'),
      visible: (options.map((opt) => opt.id).indexOf('DownloadLabelsSM') !== -1),
    },
    {
      id: 'DownloadLabelsLG',
      label: t('DownloadLabelsLG'),
      icon: 'DownloadIcon',
      action: gotoDownloadLabelsLG,
      disabled: checkDisabled(data.actions, 'DownloadLabelsLG'),
      visible: (options.map((opt) => opt.id).indexOf('DownloadLabelsLG') !== -1),
    },
    {
      id: 'DuplicateOrder',
      label: t('DuplicateOrder'),
      icon: 'DuplicateIcon',
      action: gotoDuplicateOrder,
      disabled: checkDisabled(data.actions, 'duplicateOrder'),
      visible: (options.map((opt) => opt.id).indexOf('DuplicateOrder') !== -1),
    },
    {
      id: 'ModifyOrder',
      label: t('Modify'),
      icon: 'EditIcon',
      action: gotoModifyOrder,
      disabled: checkDisabled(data.actions, 'modifyOrder'),
      visible: (options.map((opt) => opt.id).indexOf('ModifyOrder') !== -1),
    },
    {
      id: 'CancelOrder',
      label: t('CancelShipment'),
      icon: 'XIcon',
      action: gotoCancelOrder,
      disabled: checkDisabled(data.actions, 'cancelShipment'),
      visible: (options.map((opt) => opt.id).indexOf('CancelOrder') !== -1),
    },
  ];

  return (<ActionsMenu options={actionsCollection} />);
};

export const NameAndLocation = (props) => {
  const { data } = props;

  if (data.name) {
    return (
      <div>
        <h4><b>{data.name}</b></h4>
        <p>{`${data.address1 ? `${data.address1}` : ''}${data.address2 ? ` ${data.address2}` : ''},`}</p>
        <p>{`${data.city ? `${data.city}, ` : ''} ${data.provinceCode ? ` ${data.provinceCode}` : ''}`}</p>
        <p>{data.postalCode}</p>
      </div>
    );
  }

  return null;
};

export const BooleanCol = (props) => {
  const { data, params } = props;

  const valueToShow = (data[params.field]) ? params.ifTrue : params.ifFalse;

  return (<span>{valueToShow}</span>);
};

TimeCellComponent.propTypes = {
  time: PropTypes.string,
};

ExpandCellComponent.propTypes = {
  reverse: PropTypes.bool,
};

OrderStatusCellComponent.propTypes = {
  percentage: PropTypes.number,
  status: PropTypes.string,
  color: PropTypes.string,
};

ActionsDropdown.propTypes = {
  data: PropTypes.array,
  options: PropTypes.array,
};

NameAndLocation.propTypes = {
  data: PropTypes.array,
};

BooleanCol.propTypes = {
  data: PropTypes.array,
  params: PropTypes.object,
};
